import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Star, Sparkles, BookOpen, Mic, Sigma, MessageCircle, LogOut } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Mashy from "@/components/Mashy";
import { ENGAGEMENT_COPY, BADGE_PLACEHOLDERS, pick } from "@/lib/mockData";

function StatPill({ icon: Icon, value, label, color, testId }) {
  return (
    <div className="card-chunky px-4 py-3 flex items-center gap-3 flex-1" data-testid={testId}>
      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: color + "22" }}>
        <Icon className="w-5 h-5" strokeWidth={3} style={{ color }} />
      </div>
      <div>
        <div className="font-display text-xl leading-none">{value}</div>
        <div className="text-xs text-gray-500 font-bold uppercase tracking-wider">{label}</div>
      </div>
    </div>
  );
}

function QuickTile({ to, label, icon: Icon, color, testId }) {
  const nav = useNavigate();
  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      onClick={() => nav(to)}
      data-testid={testId}
      className="card-chunky p-5 flex flex-col items-center gap-2 active:translate-y-1 transition-all"
      style={{ borderColor: color + "55" }}
    >
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: color }}>
        <Icon className="w-7 h-7 text-white" strokeWidth={2.8} />
      </div>
      <div className="font-display text-base">{label}</div>
    </motion.button>
  );
}

export default function Dashboard() {
  const { user, profile, logout } = useAuth();
  const [daily, setDaily] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/lessons/daily").then((r) => setDaily(r.data.lesson)).catch(() => {});
  }, []);

  if (!user) return null;
  const xp = profile?.total_xp ?? 0;
  const streak = profile?.current_streak ?? 0;
  const welcomeMsg = ENGAGEMENT_COPY.welcomeBack[0];
  const challenge = pick(ENGAGEMENT_COPY.dailyChallenge);

  return (
    <div className="min-h-screen bg-[#F7F9FC] pb-24" data-testid="dashboard-screen">
      <header className="px-5 pt-6 pb-3 flex items-center justify-between max-w-xl mx-auto">
        <div className="flex items-center gap-3">
          <Mashy mood="happy" size={56} bounce={false} />
          <div>
            <div className="text-xs text-gray-500 font-bold">Namaste,</div>
            <div className="font-display text-lg leading-tight">{user.full_name?.split(" ")[0] || "Friend"}!</div>
          </div>
        </div>
        <button onClick={logout} data-testid="dashboard-logout" className="text-gray-400 hover:text-gray-700">
          <LogOut className="w-5 h-5" />
        </button>
      </header>

      <div className="max-w-xl mx-auto px-5 flex flex-col gap-5">
        <motion.div
          initial={{ y: -8, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="card-chunky p-4 flex items-center gap-3 bg-[#FFFBEB] border-[#FFC800]"
          data-testid="welcome-back-card"
        >
          <Mashy mood="celebrate" size={56} bounce={false} />
          <div className="flex-1">
            <div className="text-xs font-bold uppercase tracking-widest text-[#A57F00]">Welcome Back</div>
            <div className="font-display text-base text-gray-800">{welcomeMsg}</div>
            <div className="text-xs text-gray-500 mt-0.5">Daily challenge: {challenge}</div>
          </div>
        </motion.div>

        <div className="flex gap-3">
          <StatPill icon={Flame} value={streak} label="Streak" color="#FF9600" testId="stat-streak" />
          <StatPill icon={Star} value={xp} label="XP" color="#FFC800" testId="stat-xp" />
          <StatPill icon={Sparkles} value={profile?.daily_goal_minutes || 10} label="Daily Min" color="#1CB0F6" testId="stat-daily" />
        </div>

        {daily && (
          <motion.div
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="card-chunky p-5 bg-gradient-to-br from-[#1CB0F6] to-[#0EA5E9] text-white border-[#0d8fd0]"
            data-testid="daily-lesson-card"
          >
            <div className="text-xs uppercase tracking-widest font-bold opacity-90">Today's Lesson</div>
            <div className="font-display text-2xl mt-1">{daily.title}</div>
            <div className="text-sm opacity-90 mt-1">{daily.topic} • 2–5 mins</div>
            <button
              onClick={() => nav(`/lesson/${daily.lesson_id}`)}
              data-testid="start-daily-lesson"
              className="btn-secondary mt-4"
            >
              Start Lesson
            </button>
          </motion.div>
        )}

        <div>
          <div className="font-display text-lg mb-3">Quick Practice</div>
          <div className="grid grid-cols-2 gap-3">
            <QuickTile to="/lesson/lsn_daily_en" label="English" icon={BookOpen} color="#58CC02" testId="tile-english" />
            <QuickTile to="/math" label="Math" icon={Sigma} color="#1CB0F6" testId="tile-math" />
            <QuickTile to="/voice" label="Voice Practice" icon={Mic} color="#FF9600" testId="tile-voice" />
            <QuickTile to="/tutor" label="Ask Mashy" icon={MessageCircle} color="#CE82FF" testId="tile-tutor" />
          </div>
        </div>

        <div>
          <div className="font-display text-lg mb-3">Your Badges</div>
          <div className="grid grid-cols-4 gap-3" data-testid="badges-grid">
            {BADGE_PLACEHOLDERS.map((b) => (
              <div
                key={b.id}
                data-testid={`badge-${b.id}`}
                className={`card-chunky p-3 flex flex-col items-center text-center gap-1 ${b.unlocked ? "" : "opacity-40 grayscale"}`}
              >
                <div className="text-2xl">{b.icon}</div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 leading-tight">{b.label}</div>
              </div>
            ))}
          </div>
        </div>

        {user.role !== "student" && (
          <div className="card-chunky p-4 flex flex-col gap-2">
            <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Switch Workspace</div>
            <div className="flex gap-2">
              {(user.role === "teacher" || user.role === "admin") && (
                <button onClick={() => nav("/teacher")} className="btn-ghost flex-1" data-testid="goto-teacher">Teacher View</button>
              )}
              {(user.role === "course_creator" || user.role === "admin") && (
                <button onClick={() => nav("/creator/modules")} className="btn-ghost flex-1" data-testid="goto-creator">Creator Studio</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
